import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  CalendarDays, Clock, Loader2, Search, RefreshCw, Ticket, User, Edit, XCircle, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { apiGet, apiPost, apiPut, apiDelete } from '../api';
import { formatApiError } from '../lib/apiError';

export const StudioBookingsPage = () => {
  const { api } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [refundingId, setRefundingId] = useState(null);

  // Edit booking state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editForm, setEditForm] = useState({ booking_status: '', notes: '', class_id: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Cancel booking state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  // Iter-46aq: studio admin chooses whether to refund the client. Defaults to
  // TRUE because the previous hard-coded `false` was the silent root cause
  // of "the client never got refunded" complaints.
  const [refundClient, setRefundClient] = useState(true);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/studio/bookings', {
        query: statusFilter !== 'all' ? { booking_status: statusFilter } : undefined,
      });
      setBookings(data || []);
    } catch (e) {
      console.error('Failed to fetch bookings:', e);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchClasses = useCallback(async () => {
    try {
      const data = await apiGet('/api/studio/classes');
      setClasses((data || []).filter(c => c.status === 'active'));
    } catch (e) {
      console.error('Failed to fetch classes:', e);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
    fetchClasses();
  }, [fetchBookings, fetchClasses]);

  const openEditDialog = (booking) => {
    setSelectedBooking(booking);
    setEditForm({
      booking_status: booking.status,
      notes: booking.notes || '',
      class_id: booking.class_id
    });
    setShowEditDialog(true);
  };

  const openCancelDialog = (booking) => {
    setSelectedBooking(booking);
    setCancelReason('');
    // Default refundClient = true; uncheck only for free bookings where there's nothing to refund.
    const hasPayment = !!booking.payment_id || (booking.price_paid || 0) > 0 || booking.used_class_pass;
    setRefundClient(hasPayment);
    setShowCancelDialog(true);
  };

  const handleEditBooking = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await apiPut('/api/studio/bookings/{booking_id}', {
        params: { booking_id: selectedBooking.id },
        body: editForm,
      });
      toast.success('Booking updated successfully');
      setShowEditDialog(false);
      fetchBookings();
    } catch (err) {
      const detail = formatApiError(err, '');
      toast.error(detail || 'Failed to update booking');
    } finally {
      setEditLoading(false);
    }
  };

  // Iter-46aq: map the API's `refund_outcome` tag to a clear, channel-correct toast.
  const showCancellationToast = (data) => {
    const detail = data?.refund_details || 'Booking cancelled';
    switch (data?.refund_outcome) {
      case 'refunded_card':
        toast.success(`✅ ${detail}`);
        break;
      case 'refunded_pass':
        toast.success(`✅ ${detail}`);
        break;
      case 'card_refund_failed':
        toast.error(
          'Card refund failed at the payment processor. The client has been notified; ' +
          'use "Retry Refund" on the booking row once the issue is resolved.',
          { duration: 8000 }
        );
        break;
      case 'free_booking':
      case 'studio_declined_refund':
        toast.success(detail);
        break;
      case 'within_notice':
        toast.warning(detail);
        break;
      default:
        toast.success(detail);
    }
  };

  const handleCancelBooking = async () => {
    setCancelLoading(true);
    const loadingToastId = toast.loading('Processing cancellation…');
    try {
      const data = await apiDelete('/api/studio/clients/{client_id}/bookings/{booking_id}', {
        params: { client_id: selectedBooking.user_id, booking_id: selectedBooking.id },
        query: { refund: refundClient ? 'true' : 'false' },
      });
      toast.dismiss(loadingToastId);
      showCancellationToast(data);
      setShowCancelDialog(false);
      fetchBookings();
    } catch (err) {
      toast.dismiss(loadingToastId);
      const msg = formatApiError(err, 'Failed to cancel booking');
      toast.error(msg);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleRetryRefund = async (booking) => {
    if (!window.confirm(
      `Retry the card refund for ${booking.user_name || booking.user_email}? ` +
      `RyftPay previously rejected this refund — make sure the underlying issue is resolved.`
    )) return;
    setRefundingId(booking.id);
    const loadingToastId = toast.loading('Retrying card refund…');
    try {
      const data = await apiPost('/api/studio/clients/{client_id}/bookings/{booking_id}/retry-refund', {
        params: { client_id: booking.user_id, booking_id: booking.id },
      });
      toast.dismiss(loadingToastId);
      toast.success(`✅ ${data?.refund_details || 'Refund issued to client\'s card'}`);
      fetchBookings();
    } catch (err) {
      toast.dismiss(loadingToastId);
      toast.error(formatApiError(err, 'Card refund retry failed'), { duration: 8000 });
    } finally {
      setRefundingId(null);
    }
  };

  const handleRefund = async (bookingId) => {
    if (!window.confirm('Are you sure you want to refund this booking? This will restore the ClassPass credit.')) return;
    setRefundingId(bookingId);
    const loadingToastId = toast.loading('Processing refund…');
    try {
      await apiPost('/api/studio/bookings/{booking_id}/refund', {
        params: { booking_id: bookingId },
      });
      toast.dismiss(loadingToastId);
      toast.success('Booking refunded successfully');
      fetchBookings();
    } catch (err) {
      toast.dismiss(loadingToastId);
      toast.error(formatApiError(err, 'Failed to refund booking'));
    } finally {
      setRefundingId(null);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      booking.user_name?.toLowerCase().includes(term) ||
      booking.user_email?.toLowerCase().includes(term) ||
      booking.class_name?.toLowerCase().includes(term)
    );
  });

  const stats = {
    total: bookings.length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
    refunded: bookings.filter(b => b.cancellation_refunded).length
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="studio-bookings-page">
        <div>
          <h1 className="text-3xl font-bold mb-2">Bookings Management</h1>
          <p className="text-muted-foreground">View and manage all bookings, cancellations, and refunds</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Bookings</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Confirmed</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.confirmed}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Cancelled</p>
              <p className="text-2xl font-bold text-amber-600">{stats.cancelled}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Refunded</p>
              <p className="text-2xl font-bold text-blue-600">{stats.refunded}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder="Search by name, email, or class..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="bookings-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Bookings</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchBookings}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bookings List */}
        <Card>
          <CardHeader>
            <CardTitle>Bookings ({filteredBookings.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredBookings.length > 0 ? (
              <div className="space-y-4">
                {filteredBookings.map((booking, i) => (
                  <div 
                    key={booking.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                    data-testid={`booking-row-${i}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={
                          booking.status === 'confirmed' ? 'default' :
                          booking.status === 'cancelled' ? 'secondary' : 'outline'
                        }>
                          {booking.status}
                        </Badge>
                        {booking.cancellation_refunded && (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-600" data-testid={`refunded-badge-${i}`}>
                            Refunded
                          </Badge>
                        )}
                        {booking.refund_outcome === 'card_refund_failed' && (
                          <Badge variant="outline" className="text-red-600 border-red-600" data-testid={`refund-failed-badge-${i}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Refund Failed
                          </Badge>
                        )}
                        {booking.used_class_pass && (
                          <Badge variant="outline">
                            <Ticket className="w-3 h-3 mr-1" />
                            ClassPass
                          </Badge>
                        )}
                        <h4 className="font-semibold">{booking.class_name}</h4>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {booking.user_name} ({booking.user_email})
                        </span>
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-4 h-4" />
                          {booking.class_start_time ? format(new Date(booking.class_start_time), 'MMM d, yyyy') : 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {booking.class_start_time ? format(new Date(booking.class_start_time), 'h:mm a') : 'N/A'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Booked: {format(new Date(booking.booked_at), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {booking.refund_outcome === 'card_refund_failed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => handleRetryRefund(booking)}
                          disabled={refundingId === booking.id}
                          data-testid={`retry-refund-btn-${i}`}
                        >
                          {refundingId === booking.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Retry Refund
                            </>
                          )}
                        </Button>
                      )}
                      {booking.status === 'cancelled' && !booking.cancellation_refunded && booking.used_class_pass && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleRefund(booking.id)}
                          disabled={refundingId === booking.id}
                          data-testid={`refund-btn-${i}`}
                        >
                          {refundingId === booking.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Refund
                            </>
                          )}
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openEditDialog(booking)}
                        data-testid={`edit-btn-${i}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      {booking.status === 'confirmed' && (
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => openCancelDialog(booking)}
                          data-testid={`cancel-btn-${i}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No bookings found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Booking Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Booking</DialogTitle>
            <DialogDescription>
              {selectedBooking?.class_name} - {selectedBooking?.user_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditBooking} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editForm.booking_status} onValueChange={(v) => setEditForm({ ...editForm, booking_status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="no_show">No Show</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Move to Different Class (Optional)</Label>
              <Select value={editForm.class_id} onValueChange={(v) => setEditForm({ ...editForm, class_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Keep current class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={selectedBooking?.class_id || ''}>Keep Current Class</SelectItem>
                  {classes.filter(c => c.id !== selectedBooking?.class_id).map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} - {c.start_time ? format(new Date(c.start_time), 'MMM d, h:mm a') : 'TBD'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={editForm.notes} 
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Add any notes about this booking..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={editLoading}>
                {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this booking for {selectedBooking?.user_name}?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-3 bg-muted rounded-lg mb-4">
              <p className="font-medium">{selectedBooking?.class_name}</p>
              <p className="text-sm text-muted-foreground">
                {selectedBooking?.class_start_time ? format(new Date(selectedBooking.class_start_time), 'MMM d, yyyy h:mm a') : 'N/A'}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Cancellation Reason (Optional)</Label>
              <Textarea 
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancellation..."
                rows={2}
              />
            </div>
            {/* Iter-46aq: explicit refund choice — defaults to TRUE so studio
                admins don't accidentally cancel without refunding their client. */}
            <div className="flex items-start gap-3 mt-4 p-3 rounded-lg border bg-muted/30">
              <Checkbox
                id="refund-client-checkbox"
                checked={refundClient}
                onCheckedChange={(c) => setRefundClient(c === true)}
                data-testid="refund-client-checkbox"
                className="mt-0.5"
              />
              <div className="flex-1">
                <Label htmlFor="refund-client-checkbox" className="cursor-pointer">
                  Refund the client
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {selectedBooking?.used_class_pass
                    ? 'Returns 1 credit to their ClassPass.'
                    : (selectedBooking?.price_paid || 0) > 0
                      ? `Refunds €${Number(selectedBooking.price_paid).toFixed(2)} back to their card. Failures show a Retry Refund button on the row.`
                      : 'Nothing to refund (free booking).'}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowCancelDialog(false)}>Keep Booking</Button>
            <Button variant="destructive" onClick={handleCancelBooking} disabled={cancelLoading}>
              {cancelLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
